import { useState, useRef } from 'react';
import PaymentStatusBadge from './PaymentStatusBadge';
import PaymentTimer from './PaymentTimer';

const PaymentActionsPanel = ({
  appointment,
  activePayments = [],
  onRequestPayment,
  onCancelPayment,
  onViewPayment,
  loading = false
}) => {
  const [actionLoading, setActionLoading] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Ref to prevent double-click race condition
  const isRequestingPaymentRef = useRef(false);

  // Get the latest payment for this appointment
  // Use appointmentIds array (works for both single and batch payments)
  const latestPayment = activePayments
    .filter(payment =>
      payment.appointmentIds &&
      Array.isArray(payment.appointmentIds) &&
      payment.appointmentIds.includes(appointment.id)
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

  const paymentStatus = latestPayment?.status || appointment.payment_status || 'not_requested';

  const canRequestPayment = () => {
    const allowedStatuses = ['not_requested', 'failed', 'expired', 'cancelled'];
    return allowedStatuses.includes(paymentStatus);
  };

  const canCancelPayment = () => {
    return paymentStatus === 'pending' && latestPayment;
  };

  const canViewPayment = () => {
    return paymentStatus === 'completed';
  };

  const handleRequestPayment = async () => {
    // Guard against double-click using ref (synchronous check)
    if (isRequestingPaymentRef.current) return;
    if (!onRequestPayment || !canRequestPayment()) return;

    // Set ref immediately (synchronous) to block subsequent clicks
    isRequestingPaymentRef.current = true;
    setActionLoading('request');
    try {
      await onRequestPayment(appointment);
    } catch (error) {
      console.error('Error requesting payment:', error);
    } finally {
      setActionLoading(null);
      isRequestingPaymentRef.current = false;
    }
  };

  const handleCancelPayment = async () => {
    if (!onCancelPayment || !canCancelPayment()) return;
    
    if (!window.confirm('האם אתה בטוח שברצונך לבטל את בקשת התשלום?')) {
      return;
    }

    setActionLoading('cancel');
    try {
      await onCancelPayment(latestPayment.id);
    } catch (error) {
      console.error('Error cancelling payment:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewPayment = () => {
    if (!onViewPayment || !canViewPayment()) return;
    onViewPayment(latestPayment);
  };

  const handleCopyPaymentLink = async () => {
    if (!latestPayment?.paymentUrl) return;

    try {
      await navigator.clipboard.writeText(latestPayment.paymentUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy payment link:', err);
    }
  };

  const formatPaymentAmount = (amount) => {
    const paymentAmount = amount || appointment.payment_amount || appointment.price;
    return `₪${paymentAmount}`;
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">סטטוס תשלום</h4>
        <PaymentStatusBadge paymentStatus={paymentStatus} />
      </div>

      {/* Payment Details */}
      <div className="text-sm text-gray-600">
        <div className="flex justify-between">
          <span>סכום:</span>
          <span className="font-medium">{formatPaymentAmount(latestPayment?.amount)}</span>
        </div>
        
        {paymentStatus === 'completed' && appointment.payment_completed_at && (
          <div className="flex justify-between mt-1">
            <span>תאריך תשלום:</span>
            <span className="font-medium">
              {new Date(appointment.payment_completed_at).toLocaleDateString('he-IL')}
            </span>
          </div>
        )}

        {paymentStatus === 'completed' && appointment.payment_method && (
          <div className="flex justify-between mt-1">
            <span>אמצעי תשלום:</span>
            <span className="font-medium">
              {getPaymentMethodName(appointment.payment_method)}
            </span>
          </div>
        )}
      </div>

      {/* Payment Timer for pending payments */}
      {paymentStatus === 'pending' && latestPayment?.expiresAt && (
        <PaymentTimer 
          expiresAt={latestPayment.expiresAt}
          onExpired={() => {
            // Payment expired, could trigger a refresh or update
            console.log('Payment expired');
          }}
        />
      )}

      {/* Payment Actions */}
      <div className="flex flex-col space-y-2">
        {canRequestPayment() && (
          <button
            onClick={handleRequestPayment}
            disabled={loading || actionLoading === 'request'}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:from-pink-600 hover:to-purple-700 focus:ring-4 focus:ring-pink-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {actionLoading === 'request' ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin ml-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                 יוצר קישור לתשלום ושולח ללקוח...
              </span>
            ) : (
              '💳 בקש תשלום'
            )}
          </button>
        )}

        {canCancelPayment() && (
          <button
            onClick={handleCancelPayment}
            disabled={loading || actionLoading === 'cancel'}
            className="w-full bg-gray-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-600 focus:ring-4 focus:ring-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {actionLoading === 'cancel' ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 ml-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                מבטל...
              </span>
            ) : (
              '🚫 בטל בקשת תשלום'
            )}
          </button>
        )}

        {canViewPayment() && (
          <button
            onClick={handleViewPayment}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-600 focus:ring-4 focus:ring-blue-200 transition-all duration-200"
          >
           התקבל תשלום. חשבונית בהפקה...
          </button>
        )}

        {paymentStatus === 'pending' && latestPayment?.paymentUrl && (
          <button
            onClick={handleCopyPaymentLink}
            className={`w-full text-white py-2 px-4 rounded-lg font-medium focus:ring-4 transition-all duration-200 ${
              copySuccess
                ? 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-200'
                : 'bg-green-500 hover:bg-green-600 focus:ring-green-200'
            }`}
          >
            {copySuccess ? (
              <>
                <svg className="inline-block w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                הקישור הועתק!
              </>
            ) : (
              <>
                <svg className="inline-block w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                העתק קישור תשלום
              </>
            )}
          </button>
        )}
      </div>

      {/* Payment Status Messages */}
      {paymentStatus === 'failed' && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
          ⚠️ התשלום נכשל. ניתן לנסות שוב.
        </div>
      )}

      {paymentStatus === 'expired' && (
        <div className="text-sm text-orange-600 bg-orange-50 p-2 rounded">
          ⏰ קישור התשלום פג תוקף. ניתן לבקש קישור חדש.
        </div>
      )}

      {paymentStatus === 'pending' && (
        <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
          💡 הקישור נשלח ללקוח. לאחר פתיחת הקישור, הוא תקף למשך 10 דקות.
        </div>
      )}
    </div>
  );
};

// Helper function to get payment method display name
const getPaymentMethodName = (method) => {
  const methods = {
    'credit_card': 'כרטיס אשראי',
    'bit': 'ביט',
    'apple_pay': 'Apple Pay',
    'google_pay': 'Google Pay',
    'bank_transfer': 'העברה בנקאית'
  };
  return methods[method] || method;
};

export default PaymentActionsPanel;